import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import * as api from '../services/api';
import Header from '../components/Header';
import './Calendar.css';

const WEEKDAYS = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'];
const MONTHS = ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'December'];

function Calendar() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const { token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.getAllBookings().then(data => {
      const bookings = data.bookings || data;
      setOrders((Array.isArray(bookings) ? bookings : []).map(b => ({
        ...b,
        reference: b.booking_reference || `#${b.booking_id}`,
        sender_name: b.pickup_name,
        recipient_name: b.delivery_name,
      })));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [token]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday = 0

  const daysInMonth = lastDay.getDate();
  const weeks = [];
  let week = new Array(startOffset).fill(null);

  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  const getOrdersForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return orders.filter(o => o.pickup_date?.startsWith(dateStr));
  };

  const getDaySummary = (dayOrders) => {
    let eu = 0, half = 0, quarter = 0, qty = 0;
    dayOrders.forEach(o => {
      const lines = (o.lines && o.lines.length > 0) ? o.lines : [o];
      lines.forEach(l => {
        qty += Number(l.quantity) || 0;
        eu += Number(l.pallet_eu) || 0;
        half += Number(l.pallet_half) || 0;
        quarter += Number(l.pallet_quarter) || 0;
      });
    });
    const parts = [];
    if (eu) parts.push(`${eu} EU`);
    if (half) parts.push(`${half} ½`);
    if (quarter) parts.push(`${quarter} ¼`);
    return { qty, palletText: parts.join(', ') };
  };

  const today = new Date();
  const isToday = (day) => day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  const selectedOrders = selectedDate ? getOrdersForDay(selectedDate) : [];

  return (
    <div className="dashboard">
      <Header />
      <div className="dashboard-nav">
        <div className="nav-tabs">
          <button className={`nav-tab ${location.pathname === '/' ? 'active' : ''}`} onClick={() => navigate('/')}>{t('overview')}</button>
          <button className={`nav-tab ${location.pathname === '/orders' ? 'active' : ''}`} onClick={() => navigate('/orders')}>{t('orders')}</button>
          <button className={`nav-tab ${location.pathname === '/calendar' ? 'active' : ''}`} onClick={() => navigate('/calendar')}>{t('calendar')}</button>
          <button className={`nav-tab ${location.pathname === '/insights' ? 'active' : ''}`} onClick={() => navigate('/insights')}>{t('insights')}</button>
          <button className={`nav-tab ${location.pathname === '/administration' ? 'active' : ''}`} onClick={() => navigate('/administration')}>{t('administration')}</button>
        </div>
      </div>

      <div className="calendar-page-content">
        <div className="calendar-container">
          <div className="calendar-header">
            <button className="cal-nav-btn" onClick={prevMonth}>&lt;</button>
            <h2 className="cal-month-title">{MONTHS[month]} {year}</h2>
            <button className="cal-nav-btn" onClick={nextMonth}>&gt;</button>
            <button className="cal-today-btn" onClick={goToday}>{t('today')}</button>
          </div>

          {loading ? (
            <p className="loading-text">{t('loading')}</p>
          ) : (
            <table className="cal-grid">
              <thead>
                <tr>
                  {WEEKDAYS.map(d => <th key={d}>{d}</th>)}
                </tr>
              </thead>
              <tbody>
                {weeks.map((week, wi) => (
                  <tr key={wi}>
                    {week.map((day, di) => {
                      const dayOrders = getOrdersForDay(day);
                      const isSelected = day === selectedDate;
                      return (
                        <td
                          key={di}
                          className={`cal-cell ${!day ? 'empty' : ''} ${isToday(day) ? 'today' : ''} ${isSelected ? 'selected' : ''} ${dayOrders.length > 0 ? 'has-orders' : ''}`}
                          onClick={() => day && setSelectedDate(day === selectedDate ? null : day)}
                        >
                          {day && (() => {
                            const summary = dayOrders.length > 0 ? getDaySummary(dayOrders) : null;
                            return (
                              <>
                                <span className="cal-day-num">{day}</span>
                                {summary && (
                                  <div className="cal-day-info">
                                    <span className="cal-day-bookings">{dayOrders.length} ordre{dayOrders.length !== 1 ? 'r' : ''}</span>
                                    {summary.palletText && <span className="cal-day-pallets">{summary.palletText}</span>}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selectedDate && (
          <div className="calendar-detail">
            <h3>{selectedDate}. {MONTHS[month]} — {selectedOrders.length} booking{selectedOrders.length !== 1 ? 'er' : ''}</h3>
            {selectedOrders.length === 0 ? (
              <p className="no-data">Ingen bookinger denne dag</p>
            ) : (
              <div className="cal-order-list">
                {selectedOrders.map(o => (
                  <div key={o.id} className="cal-order-card">
                    <div className="cal-order-top">
                      <span className="cal-order-ref">{o.reference}</span>
                      <span className={`status-badge ${o.approval_status === 'REJECTED' ? 'status-rejected' : o.approval_status === 'PENDING_APPROVAL' ? 'status-pending' : ''}`}>
                        {o.approval_status === 'APPROVED' ? 'Godkendt' : o.approval_status === 'REJECTED' ? 'Afvist' : 'Afventer'}
                      </span>
                    </div>
                    <div className="cal-order-route">{o.sender_name} → {o.recipient_name}</div>
                    <div className="cal-order-time">{o.pickup_time || ''}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Calendar;
